

import ChecklistsTab from '@components/ProjectOverview/Checklist/Checklist';
import InspectionsTab from '@components/ProjectOverview/Inspections/Inspections';
import { ButtonContainer, InfoBoxContainer, ProjectAvatar, ProjectContainer, ProjectHeaderContainer, ProjectInfoContainer, ProjectTitle, ProjectTitleContainer, ProjectTitleWrapper, TabsContainer } from '@components/ProjectOverview/Overview/Overview.styled';
import { OverviewTab } from '@components/ProjectOverview/Overview/Overview.tsx';
import ProjectUploadModal from '@components/Projects/UploadModal';
import InfoBox from '@components/Shared/InfoBox';
import Header from '@components/Shared/Layout/Header';
import { useLocalization } from '@lib/localization';
import { has } from '@lib/permission';
import { buildProjectPath, type ProjectTab, VIEWER_BASE } from '@lib/routing/projectPath';
import { useProjectRoute } from '@lib/routing/useProjectRoute';
import { Check as CheckIcon, ContentCopy as ContentCopyIcon, OpenInBrowser } from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Tab, Tabs, Tooltip, Typography, useMediaQuery, useTheme
} from '@mui/material';
import { type IInspection, useLazyGetInspectionsQuery } from '@store/api/inspectionEndpoints';
import { folderPathOf, thumbnailCache, useGetProjectQuery, useLazyGetProjectThumbnailQuery, useUpdateProjectMutation } from '@store/api/projectEndpoints';
import { useLoading } from '@store/hooks';
import { useEffect, useId, useState } from 'react';
import { CubeIcon, UploadIcon } from 'react-line-awesome';
import { useNavigate } from 'react-router-dom';

/** Tab index order, so the URL and the MUI Tabs value translate one way in one place. */
const TAB_ORDER: ProjectTab[] = ['overview', 'inspections', 'checklists'];

// Sits with the title rather than competing with the header's actions, so it reads as part
// of the project's identity — which is what the id it copies is.
const copyIdButtonStyles = { color: '#aaa', fontSize: 16, '&:focus': { outline: 'none' } };

interface DashBoardTabsProps {
  value: number
  onChange: (event: React.SyntheticEvent, newValue: number) => void
}

const DashboardTabs: React.FC<DashBoardTabsProps> = ({ value, onChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useLocalization();

  return (
    <TabsContainer>
      <Tabs
        value={value}
        onChange={onChange}
        indicatorColor="primary"
        textColor="primary"
        variant={isMobile ? 'fullWidth' : 'standard'}
      >
        <Tab label={t('Common_Overview')} style={{ minWidth: isMobile ? 'auto' : '200px' }} />
        <Tab label={t('Common_Inspections')} style={{ minWidth: isMobile ? 'auto' : '200px' }} />
        <Tab label={t('Common_Checklists')} style={{ minWidth: isMobile ? 'auto' : '200px' }} />
      </Tabs>
    </TabsContainer>
  );
};

const ProjectOverviewPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useLocalization();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [modalOpen, setModalOpen] = useState(false);
  const [isIdCopied, setIdCopied] = useState(false);

  const [updateProject, { isLoading: isUpdateProjectLoading }] = useUpdateProjectMutation();

  const [getProjectThumbnail, { isLoading: isProjectThumbnailLoading }] = useLazyGetProjectThumbnailQuery();
  const [tabValue, setTabValue] = useState(0);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | undefined>();
  const { show, hide } = useLoading();
  const { projectId, folderPath, tab, inspectionId, poiId, stepIndex, buildPath } = useProjectRoute();

  const { data: project, isLoading } = useGetProjectQuery({ id: projectId! }, { skip: !projectId });

  const canUpdateProject = has(project, 'project:put');

  const [inspections, setInspections] = useState<IInspection[]>([]);
  const [getInspections, { isLoading: isInspectionListLoading }] = useLazyGetInspectionsQuery();

  const componentId = useId();

  useEffect(() => {
    const isProjectNotReady = !project || isLoading;
    const isNotReady = isProjectNotReady || isProjectThumbnailLoading || isInspectionListLoading
      ; (isNotReady ? show : hide)(componentId);
  }, [componentId, hide, isInspectionListLoading, isLoading, isProjectThumbnailLoading, project, show]);

  useEffect(() => {
    setTabValue(TAB_ORDER.indexOf(tab));
  }, [tab]);

  // The folder prefix is context, not authority: a project moved since the link was minted
  // still resolves by id, so correct the URL in place rather than 404 on a stale path.
  useEffect(() => {
    if (!project || !projectId) return;

    const actualFolderPath = folderPathOf(project);
    if (actualFolderPath === folderPath) return;

    navigate(buildProjectPath({ folderPath: actualFolderPath, projectId, tab, inspectionId, poiId, stepIndex }),
      { replace: true });
  }, [folderPath, inspectionId, navigate, poiId, project, projectId, stepIndex, tab]);

  useEffect(() => {
    const initInspectionList = async (): Promise<void> => {
      if (!projectId || !project) {
        setInspections([]);
        setThumbnailUrl('');
        return;
      }

      try {
        const thumbnailUrl = thumbnailCache.get(projectId);
        if (thumbnailUrl != undefined) setThumbnailUrl(thumbnailUrl);

        const [inspectionsResponse, thumbnailResponse] = await Promise.all([
          getInspections({ projectId, include: ['notes', 'pointOfInterests', 'documents', 'resources'] }),
          thumbnailUrl === undefined ? getProjectThumbnail(project.projectInfo.id) : undefined,
        ]);

        setInspections(inspectionsResponse.data ?? []);
        if (thumbnailResponse) setThumbnailUrl(thumbnailResponse.data);
      } catch (error) {
        console.error('Failed to load project data:', error);
        setInspections([]);
        setThumbnailUrl('');
      }
    };

    initInspectionList();
  }, [projectId, project, getInspections, getProjectThumbnail]);

  // The confirmation is transient: the icon reverts so the button reads as an action again.
  useEffect(() => {
    if (!isIdCopied) return;

    const timer = setTimeout(() => setIdCopied(false), 1500);

    return () => clearTimeout(timer);
  }, [isIdCopied]);

  const copyProjectId = async () => {
    // The Clipboard API is absent on insecure origins, where a click is a no-op, not a crash.
    if (!projectId || !navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(projectId);
      setIdCopied(true);
    } catch {
      setIdCopied(false);
    }
  };

  const openInViewer = () => {
    // The viewer is the same URL grammar under its own base, and this page already knows
    // where the project lives — so the link carries the folder path without a lookup.
    const projectRoute = buildProjectPath({ base: VIEWER_BASE, folderPath, projectId: projectId! });
    const newWindow = window.open(projectRoute, '_blank');

    if (!newWindow) navigate(projectRoute);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    navigate(buildPath({ tab: TAB_ORDER[newValue] ?? 'overview' }));
  };

  return project ? (
    <>
      <Header />
      {
        isUpdateProjectLoading && <Alert
          severity="warning"
          icon={<CircularProgress size={20} color="warning" />}
          sx={{ width: '100%', justifyContent: 'center', marginTop: '0 !important', marginBottom: '16px !important' }}
        >
          <Typography variant="body2" fontWeight={500}>
            {t('Project_Upload_InProgress')}
          </Typography>
        </Alert>
      }

      <ProjectContainer isMobile={isMobile}>
        <ProjectAvatar src={thumbnailUrl} variant="square" isMobile={isMobile}>
          <CubeIcon size='5x' style={{ color: 'white' }} />
        </ProjectAvatar>
        <ProjectInfoContainer isMobile={isMobile}>
          <ProjectHeaderContainer isMobile={isMobile}>
            <ProjectTitleContainer isMobile={isMobile}>
              <ProjectTitleWrapper isMobile={isMobile}>
                <ProjectTitle variant="h6">{project.projectInfo.name}</ProjectTitle>
                {inspections.length > 0 && (
                  <Chip
                    label={t('Inspections_Count', { count: inspections.length })}
                    size="small"
                    sx={{ marginLeft: isMobile ? 0 : 1 }}
                  />
                )}
                <Tooltip title={isIdCopied ? t('Common_Copied') : t('Project_CopyId')}>
                  <IconButton
                    size="small"
                    onClick={copyProjectId}
                    aria-label={t('Project_CopyId')}
                    sx={copyIdButtonStyles}
                  >
                    {isIdCopied ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
                  </IconButton>
                </Tooltip>
              </ProjectTitleWrapper>
            </ProjectTitleContainer>
            <ButtonContainer isMobile={isMobile}>
              {canUpdateProject &&
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                  onClick={() => { setModalOpen(true); }}
                >
                  {t('Project_Manual_Update')}
                </Button>}
              <Button
                variant="contained"
                onClick={openInViewer}
                startIcon={<OpenInBrowser />}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
              >
                {t('Project_View')}
              </Button>
            </ButtonContainer>
          </ProjectHeaderContainer>

          <InfoBoxContainer isMobile={isMobile}>
            <InfoBox
              icon={<CalendarTodayIcon sx={{ fontSize: 16, color: '#aaa' }} />}
              text={t('Common_DateCreated')}
              value={new Date(project.projectInfo.dateCreated).toAgoString()}
            />
            <InfoBox
              icon={<CalendarTodayIcon sx={{ fontSize: 16, color: '#aaa' }} />}
              text={t('Common_DateUpdated')}
              value={new Date(project.projectInfo.dateModified).toAgoString()}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', ml: isMobile ? 0 : 'auto' }}></Box>
          </InfoBoxContainer>
        </ProjectInfoContainer>
      </ProjectContainer>
      <DashboardTabs value={tabValue} onChange={handleTabChange} />
      {tabValue === 0 && <OverviewTab inspections={inspections} />}
      {tabValue === 1 && <InspectionsTab projectId={projectId!} inspections={inspections} />}
      {tabValue === 2 && <ChecklistsTab projectId={projectId!} inspections={inspections} />}

      <ProjectUploadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        existingInspections={inspections}
        uploadProject={async (formData: FormData) => { await updateProject({ id: projectId!, formData }); }}
      />
    </>
  ) : null;
};

export default ProjectOverviewPage;
